package com.islamicmedia.shared.data.ocr

import com.islamicmedia.shared.data.api.createHttpClient
import com.islamicmedia.shared.data.api.serverBaseUrl
import com.islamicmedia.shared.domain.model.MediaCandidate
import com.islamicmedia.shared.domain.model.MediaType
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi

@Serializable
private data class ExtractTitleRequest(
    val imageBase64: String,
    val mimeType: String = "image/png"
)

@Serializable
private data class MediaCandidateDto(
    val mediaType: String? = null,
    val title: String = "",
    val confidence: Double = 0.0
)

@Serializable
private data class ExtractTitleResponse(
    val candidates: List<MediaCandidateDto> = emptyList(),
    val title: String? = null,
    val error: String? = null
)

/**
 * Extracts media titles from images using GPT-5 Nano vision.
 * Works across all platforms (Android, iOS, Web) via server proxy.
 */
class TextRecognizer {
    private val client = createHttpClient()
    private val baseUrl = serverBaseUrl()
    private val json = Json {
        ignoreUnknownKeys = true
        isLenient = true
    }

    /**
     * Identifies the media item(s) shown in a photo via the server's two-stage vision pipeline.
     * A clean scene yields one candidate; a cluttered scene (bookshelf, room with a TV + poster)
     * can yield several, ranked most-likely first.
     * @param imageBytes The image data as a byte array (PNG or JPEG)
     * @return The identified candidates (empty if nothing was recognized or the call failed).
     */
    @OptIn(ExperimentalEncodingApi::class)
    suspend fun extract(imageBytes: ByteArray): List<MediaCandidate> {
        return try {
            val base64Image = Base64.encode(imageBytes)
            val mimeType = detectMimeType(imageBytes)

            val requestBody = json.encodeToString(
                ExtractTitleRequest.serializer(),
                ExtractTitleRequest(base64Image, mimeType)
            )

            val response = client.post("$baseUrl/api/extract-title") {
                contentType(ContentType.Application.Json)
                setBody(requestBody)
            }

            if (!response.status.isSuccess()) return emptyList()

            val result = json.decodeFromString<ExtractTitleResponse>(response.bodyAsText())
            val candidates = result.candidates
                .filter { it.title.isNotBlank() }
                .map { MediaCandidate(title = it.title.trim(), mediaType = parseMediaType(it.mediaType)) }

            // Back-compat: if the server only sent a bare title, wrap it as a single candidate.
            candidates.ifEmpty {
                result.title?.takeIf { it.isNotBlank() }
                    ?.let { listOf(MediaCandidate(title = it.trim())) }
                    ?: emptyList()
            }
        } catch (e: Exception) {
            emptyList()
        }
    }

    private fun parseMediaType(raw: String?): MediaType? = when (raw?.trim()?.lowercase()) {
        "book", "books" -> MediaType.BOOKS
        "movie", "movies", "film" -> MediaType.MOVIES
        "tv show", "tv", "tv_show", "tv shows", "series" -> MediaType.TV_SHOWS
        "game", "games", "video game" -> MediaType.GAMES
        "youtube", "youtube channel" -> MediaType.YOUTUBE
        else -> null
    }

    private fun detectMimeType(imageBytes: ByteArray): String {
        // Check magic bytes for common image formats
        return when {
            imageBytes.size >= 3 &&
                imageBytes[0] == 0xFF.toByte() &&
                imageBytes[1] == 0xD8.toByte() &&
                imageBytes[2] == 0xFF.toByte() -> "image/jpeg"
            imageBytes.size >= 8 &&
                imageBytes[0] == 0x89.toByte() &&
                imageBytes[1] == 0x50.toByte() &&
                imageBytes[2] == 0x4E.toByte() &&
                imageBytes[3] == 0x47.toByte() -> "image/png"
            imageBytes.size >= 4 &&
                imageBytes[0] == 0x47.toByte() &&
                imageBytes[1] == 0x49.toByte() &&
                imageBytes[2] == 0x46.toByte() -> "image/gif"
            imageBytes.size >= 4 &&
                imageBytes[0] == 0x52.toByte() &&
                imageBytes[1] == 0x49.toByte() &&
                imageBytes[2] == 0x46.toByte() &&
                imageBytes[3] == 0x46.toByte() -> "image/webp"
            else -> "image/png" // Default
        }
    }
}
